import { MediaContent } from '@asset/type/common/type';
import { StudentsExtras } from '@asset/type/userType';
import { SendableMessageType } from '@sendbird/uikit-react/types/utils';
import { DateTime } from 'luxon';
import { create } from 'zustand';

export const useChatStore = create<{
  selectedChannel: { url: string; studentName: string };
  unreadCount: number;
  questionDetailUrl: string;
  clickedChatNotification: { channelUrl: string };
  clickedMessageToReply: SendableMessageType;
  setSelectedChannel: (channel: { url: string; studentName: string }) => void;
  setUnreadCount: (count: number) => void;
  setQuestionDetailUrl: (url: string) => void;
  setClickedChatNotification: (notification: { channelUrl: string }) => void;
  setClickedMessageToReply: (message: SendableMessageType) => void;
}>((set) => ({
  selectedChannel: null,
  unreadCount: 0,
  questionDetailUrl: null,
  clickedChatNotification: null,
  clickedMessageToReply: null,
  setSelectedChannel: (channel: { url: string; studentName: string }) =>
    set({ selectedChannel: channel }),
  setUnreadCount: (count: number) => set({ unreadCount: count }),
  setQuestionDetailUrl: (url: string) => set({ questionDetailUrl: url }),
  setClickedChatNotification: (notification: { channelUrl: string }) =>
    set({ clickedChatNotification: notification }),
  setClickedMessageToReply: (message: SendableMessageType) =>
    set({ clickedMessageToReply: message }),
}));

export const useChatPopupStore = create<{
  isChatPopupOpen: boolean;
  chatPopupRef: React.RefObject<HTMLDivElement>;
  chatPopupPosition: { x: number; y: number };
  chatPopupSize: { width: number; height: number };
  setIsChatPopupOpen: (isOpen: boolean) => void;
  setChatPopupRef: (ref: React.RefObject<HTMLDivElement>) => void;
  setChatPopupPosition: (position: { x: number; y: number }) => void;
  setChatPopupSize: (size: { width: number; height: number }) => void;
}>((set) => ({
  isChatPopupOpen: false,
  chatPopupRef: null,
  chatPopupPosition: { x: null, y: null },
  chatPopupSize: { width: null, height: null },
  setIsChatPopupOpen: (isOpen: boolean) => set({ isChatPopupOpen: isOpen }),
  setChatPopupRef: (ref: React.RefObject<HTMLDivElement>) =>
    set({ chatPopupRef: ref }),
  setChatPopupPosition: (position: { x: number; y: number }) =>
    set({ chatPopupPosition: position }),
  setChatPopupSize: (size: { width: number; height: number }) =>
    set({ chatPopupSize: size }),
}));

export const useGroupChannelListStore = create<{
  channelStudentExtraInfoMap: { [channelUrl: string]: StudentsExtras };
  setChannelStudentExtraInfoMap: (map: {
    [channelUrl: string]: StudentsExtras;
  }) => void;
}>((set) => {
  const resetData = () => set({ channelStudentExtraInfoMap: {} });
  setInterval(resetData, 60000);

  return {
    channelStudentExtraInfoMap: {},
    setChannelStudentExtraInfoMap: (map: {
      [channelUrl: string]: StudentsExtras;
    }) => set({ channelStudentExtraInfoMap: map }),
  };
});

export const useNotificationStore = create<{
  notificationPermission: NotificationPermission;
  setNotificationPermission: (permission: NotificationPermission) => void;
}>((set) => ({
  notificationPermission:
    typeof window !== 'undefined' && 'Notification' in window && Notification
      ? Notification.permission
      : 'default',
  setNotificationPermission: (permission: NotificationPermission) =>
    set({ notificationPermission: permission }),
}));

export const useChatImageMessageStore = create<{
  images: MediaContent[] | null;
  imageIndex: number;
  messageMetaInfo: {
    sender: string | null;
    sendDate: DateTime | null;
  };
  setImages: (images: MediaContent[]) => void;
  setImageIndex: (index: number) => void;
  setMessageMetaInfo: (info: { sender: string; sendDate: DateTime }) => void;
  resetStore: () => void;
}>((set) => ({
  images: null,
  imageIndex: 0,
  messageMetaInfo: {
    sender: null,
    sendDate: null,
  },
  setImages: (images: MediaContent[]) => set({ images }),
  setImageIndex: (index: number) => set({ imageIndex: index }),
  setMessageMetaInfo: ({ sender, sendDate }) =>
    set({
      messageMetaInfo: { sender, sendDate },
    }),
  resetStore: () =>
    set({
      images: null,
      imageIndex: 0,
      messageMetaInfo: {
        sender: null,
        sendDate: null,
      },
    }),
}));
